import React, { FC, Suspense, useEffect } from 'react';
import { Switch, useRouteMatch } from 'react-router-dom';
import { PermissionSetType } from 'api/models';
import { RouterShell } from 'components/organisms/router-shell';
import { AllTasksPage } from 'components/pages';
import { AllTasksBoardPage } from 'components/pages/all-tasks/board';
import { TimesheetFeatureDiscovery } from 'components/pages/timesheet';
import {
  AttachmentWorkspace,
  OverlayComparison,
  SideBySideComapre,
} from 'components/pages/workspace';
import { PermissionRuleName, RoutePaths, BEAMER_URL } from 'constant';
import {
  useIsCurrentUserCollaborator,
  useUserPermissionSetType,
} from 'hooks/permissions';
import { useAccount } from 'hooks/use-account';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useIsAppInCypress } from 'hooks/use-is-app-in-cypress';
import { useUser } from 'hooks/use-user';
import { useResourceManagementConfigQuery } from 'queries/resource-management-configuration';
import { AccountAssessment } from 'routes/account-assessment/account-assessment';
import { AuthorisedRoute as SecureRoute } from 'routes/authorised-route';
import { RespSpaceTabPage } from 'routes/responsive/resp-space-tab';
import { RespStatusUpdate } from 'routes/responsive/resp-status-update-content';
import { RespTaskDesc } from 'routes/responsive/resp-task-desc';
import { TaskDetailsPageResponsive } from 'routes/tasks/task-details-responsive';
import { getDevEnv } from 'utils/is-localhost';
import { useZapScaleVendorInitialiser } from 'vendor/zapscale';
import { NotFound } from '../404';
import { DashboardReportPage } from '../dashboard';
import { DemoPage } from '../demos';
import { FeatureFlagList } from '../feature-flags';
import { FormPreview } from '../form-preview';
import { HomePage } from '../home';
import { Notifications } from '../notifications';
import { Ozone } from '../ozone.design';
import { ProjectRoute } from '../projects';
import { ReportsRoute } from '../reports';
import { ResourceManagementRoute } from '../resource-management';
import { Scheduler } from '../scheduler';
import { SettingsRoute } from '../settings/settings';
import { TemplatesRoute } from '../templates';
import { TimesheetsRoute } from '../timesheets';
import { IntroModal } from './intro-modal';

export const VendorRoutes: FC = () => {
  const isInCypress = useIsAppInCypress();
  const { user } = useUser();
  const { isLocal } = getDevEnv();
  const account = useAccount();
  const initZapScale = useZapScaleVendorInitialiser();

  useEffect(() => {
    initZapScale(account, user);
  }, [account, initZapScale, user]);

  useEffect(() => {
    const isBeamerPresent = Boolean(
      document.querySelector(`script[src="${BEAMER_URL}"]`),
    );

    if (!isInCypress && !isBeamerPresent && !isLocal) {
      const beamerScriptEl = document.createElement('script');

      beamerScriptEl.setAttribute('type', 'text/javascript');

      beamerScriptEl.setAttribute('src', BEAMER_URL);

      document.body.appendChild(beamerScriptEl);

      beamerScriptEl.onload = () => {
        window.Beamer?.update({
          user_firstname: user.firstName,
          user_lastname: user.lastName,
          user_email: user.emailId,
          user_id: user.userId,
        });
      };
    }
  }, [isInCypress, isLocal, user]);
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;
  const isSuperUser =
    currentUserPermissionSetType === PermissionSetType.SuperUser;

  const isFormTemplatePreview = useRouteMatch(RoutePaths.formTemplatePreview);
  const isAttachmentWorkspace = useRouteMatch([RoutePaths.attachmentWorkspace]);
  const { data: resourceManagementConfig } = useResourceManagementConfigQuery();
  const isCollaborator = useIsCurrentUserCollaborator();
  // to be revisited when view and edit access are differentiated
  const isEnabledForCollaborator =
    resourceManagementConfig?.accessConfig?.collaboratorAccessMeta
      ?.hasViewAccess ?? false;

  return isFormTemplatePreview ? (
    <SecureRoute
      path={RoutePaths.formTemplatePreview}
      component={FormPreview}
    />
  ) : isAttachmentWorkspace ? (
    <Switch>
      <SecureRoute
        path={RoutePaths.attachmentWorkspace}
        component={AttachmentWorkspace}
        exact
      />
      <SecureRoute
        path={RoutePaths.attachmentWorkspaceSideBySide}
        component={SideBySideComapre}
        exact
      />
      <SecureRoute
        path={RoutePaths.attachmentWorkspaceOverlay}
        component={OverlayComparison}
        exact
      />
    </Switch>
  ) : (
    <AccountAssessment>
      <RouterShell>
        <Suspense fallback="">
          <Switch>
            <SecureRoute
              path={RoutePaths.respSpacesTab}
              component={RespSpaceTabPage}
              exact
            />
            <SecureRoute
              path={RoutePaths.respStatusUpdate}
              component={RespStatusUpdate}
              exact
            />
            <SecureRoute
              path={RoutePaths.taskDesc}
              component={RespTaskDesc}
              exact
            />

            <SecureRoute
              path={RoutePaths.taskDetailsResponsive}
              component={TaskDetailsPageResponsive}
              exact
            />

            <SecureRoute path="/demos" strict component={DemoPage} />
            <SecureRoute
              path="/feature-flags"
              strict
              component={FeatureFlagList}
            />
            <SecureRoute path="/scheduler" strict component={Scheduler} />
            <SecureRoute
              path={RoutePaths.tasks}
              strict
              exact
              component={AllTasksPage}
            />
            <SecureRoute
              path={RoutePaths.tasksBoard}
              strict
              component={AllTasksBoardPage}
            />
            <SecureRoute path={RoutePaths.projects} component={ProjectRoute} />
            <SecureRoute path={RoutePaths.notifications}>
              <Notifications />
            </SecureRoute>
            <SecureRoute
              path={RoutePaths.templates}
              component={TemplatesRoute}
              ruleName={PermissionRuleName.TemplatePage}
            />
            <SecureRoute
              path={RoutePaths.reports}
              component={ReportsRoute}
              ruleName={PermissionRuleName.ReportsPage}
            />
            {isAccountAdmin || isSuperUser ? (
              <SecureRoute
                path={RoutePaths.dashboard}
                ruleName={PermissionRuleName.DashboardPage}
              >
                <DashboardReportPage />
              </SecureRoute>
            ) : null}

            <SecureRoute path={RoutePaths.settings} component={SettingsRoute} />
            {isTimesheetFeatureEnabled ? (
              <SecureRoute
                path={RoutePaths.timesheets}
                component={TimesheetsRoute}
                ruleName={PermissionRuleName.TimesheetPage}
              />
            ) : isAccountAdmin ? (
              <SecureRoute
                path={RoutePaths.timesheets}
                component={TimesheetFeatureDiscovery}
              />
            ) : null}
            <SecureRoute
              path={RoutePaths.resourceManagement}
              component={ResourceManagementRoute}
              ruleName={
                isCollaborator && isEnabledForCollaborator
                  ? undefined
                  : PermissionRuleName.ResourceManagementPage
              }
            />
            <SecureRoute path="/ozone.design" component={Ozone} />

            <SecureRoute path="/" exact>
              <HomePage />
            </SecureRoute>
            <SecureRoute path="*">
              <NotFound />
            </SecureRoute>
          </Switch>
          <IntroModal />
        </Suspense>
      </RouterShell>
    </AccountAssessment>
  );
};
